// Rules tab — doctrine modules by category, with dependency handling and
// the toggle-notice/undo affordance. Split out of CustomizeTabs.tsx.

import { MODULE_IDS, type ModuleCategory, type ModuleId, moduleCategory } from '@aod/protocol';
import { cn } from '@aod/ui/lib/utils';
import { CircleAlert, Link2, Lock, Undo2 } from 'lucide-react';
import { useId } from 'react';
import { type MessageKey, type Translate, useLocale, useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import type { Notice, TabProps } from '../CustomizeTabs.js';
import { Switch } from '../controls.js';
import {
  categoryLabel,
  formatList,
  localizeProblem,
  moduleDescription,
  moduleLabel,
} from '../presentation.js';
import {
  type SetupDraft,
  enabledDependents,
  isModuleLocked,
  missingDependencies,
  setModule,
} from '../setupModel.js';

// ── Rules ──────────────────────────────────────────────────────────────

/** Categories in registry order, each with its modules. */
export const MODULE_GROUPS: readonly { category: ModuleCategory; modules: ModuleId[] }[] = (() => {
  const groups: { category: ModuleCategory; modules: ModuleId[] }[] = [];
  for (const id of MODULE_IDS) {
    const category = moduleCategory(id);
    let group = groups.find((g) => g.category === category);
    if (!group) {
      group = { category, modules: [] };
      groups.push(group);
    }
    group.modules.push(id);
  }
  return groups;
})();

/** Notice text after a toggle, naming what came along with it. */
export function toggleNotice(
  t: Translate,
  locale: Parameters<typeof formatList>[0],
  id: ModuleId,
  added: readonly ModuleId[],
  removed: readonly ModuleId[],
): string | null {
  const name = moduleLabel(t, id);
  const list = (ids: readonly ModuleId[]) =>
    formatList(
      locale,
      ids.map((m) => moduleLabel(t, m)),
    );
  if (removed.length > 0) return t('setup.alsoOff', { name, list: list(removed) });
  if (added.length > 0) return t('setup.alsoOn', { name, list: list(added) });
  return null;
}

interface RulesTabProps extends TabProps {
  readonly problems: readonly string[];
  readonly notice: Notice | null;
  readonly onNotice: (notice: Notice | null) => void;
}

export function RulesTab({ draft, onChange, problems, notice, onNotice }: RulesTabProps) {
  const t = useT();
  const locale = useLocale();
  const kid = draft.humanAssists.uiMode === 'icons';
  const on = new Set(draft.ruleset.modules);

  const toggle = (id: ModuleId, next: boolean) => {
    const res = setModule(draft, id, next);
    onChange(res.draft);
    const text = toggleNotice(t, locale, id, res.added, res.removed);
    onNotice(text ? { text, undo: draft } : null);
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">{t('setup.core')}</p>

      {problems.length > 0 ? (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-foreground"
        >
          <div className="mb-1 flex items-center gap-1.5 font-semibold">
            <CircleAlert className="size-4 text-destructive" aria-hidden />
            {t('setup.problems')}
          </div>
          <ul className="ml-6 list-disc">
            {problems.map((p) => (
              <li key={p}>{localizeProblem(t, p)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div aria-live="polite" className="empty:hidden">
        {notice ? (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/40 bg-primary/10 p-3 text-sm text-foreground">
            <span className="flex items-start gap-2">
              <Link2 className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
              {notice.text}
            </span>
            {notice.undo ? (
              <button
                type="button"
                onClick={() => {
                  if (notice.undo) onChange(notice.undo);
                  onNotice(null);
                }}
                className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Undo2 className="size-3.5" aria-hidden />
                {t('setup.undo')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="gap-3 md:columns-2 xl:columns-3">
        {MODULE_GROUPS.map(({ category, modules }) => (
          <section
            key={category}
            aria-labelledby={`setup-cat-${category}`}
            className="mb-3 break-inside-avoid rounded-xl border border-border bg-card/70 p-3"
          >
            <h3
              id={`setup-cat-${category}`}
              className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground"
            >
              {categoryLabel(t, category)}
            </h3>
            <ul className="flex flex-col gap-3">
              {modules.map((id) => (
                <ModuleRow
                  key={id}
                  id={id}
                  draft={draft}
                  checked={on.has(id)}
                  kid={kid}
                  onToggle={toggle}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

interface ModuleRowProps {
  readonly id: ModuleId;
  readonly draft: SetupDraft;
  readonly checked: boolean;
  readonly kid: boolean;
  readonly onToggle: (id: ModuleId, next: boolean) => void;
}

function ModuleRow({ id, draft, checked, kid, onToggle }: ModuleRowProps) {
  const t = useT();
  const locale = useLocale();
  const domId = useId();
  const locked = !checked && isModuleLocked(id, draft.ruleset.content);
  const needs = checked ? [] : missingDependencies(draft, id);
  const dependentsOn = checked ? enabledDependents(draft, id) : [];
  const label = moduleLabel(t, id);
  const names = (ids: readonly ModuleId[]) =>
    formatList(
      locale,
      ids.map((m) => moduleLabel(t, m)),
    );
  const warning =
    dependentsOn.length > 0
      ? t('setup.alsoOff', { name: label, list: names(dependentsOn) })
      : undefined;
  const testid = `setup-module-${id.replace('.', '-')}`;
  return (
    <li className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium leading-tight text-foreground">{label}</div>
        <p id={`${domId}-desc`} className="text-xs text-muted-foreground">
          {moduleDescription(t, id, kid)}
        </p>
        {locked ? (
          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
            <Lock className="size-3" aria-hidden />
            {t('setup.lockedFamily')}
          </span>
        ) : needs.length > 0 ? (
          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
            <Link2 className="size-3" aria-hidden />
            {t('setup.needs', { list: names(needs) })}
          </span>
        ) : null}
      </div>
      <Switch
        checked={checked}
        onChange={(next) => onToggle(id, next)}
        label={label}
        describedBy={`${domId}-desc`}
        title={warning}
        disabled={locked}
        className="mt-0.5"
        data-testid={testid}
        {...appDom({ id: testid, type: 'Toggle', role: 'input', scope: 'main', label })}
      />
    </li>
  );
}
